import React from "react";
import { NavigationProp, ParamListBase } from "@react-navigation/core";
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { useSelector } from "react-redux";
import firebase from "firebase/app";
import { v4 as uuidv4 } from "uuid";

import { kColors } from "../kColors";
import { PdCenter, PdDivider, PdRow } from "src/components/v2/ui/core";
import { tImage, tProduct, tProductCategory, tVariant } from "tmt_api/src/types/types";
import { documentBrandProduct } from "src/db/brand.db";
import { FieldArrayWithId, FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { PdStack, PdText } from "src/components/layout";
import { PdFormCard, PdSectionBadge, PdSectionBadgeLabel } from "./P09_Profile/components";
import { kStyleConst } from "src/kStyle";
import { PdFormErrorText, PdFormLabel } from "src/components/v2/forms/control";
import {
  PdHookFormCheckbox,
  PdHookFormErrorMessage,
  PdHookFormImagePicker,
  PdHookFormInput,
  PdHookFormInputFloat,
  PdHookFormInputMultiple,
  PdHookFormInputNumber,
  PdHookFormSelect,
  PdHookFormSwitch,
} from "src/components/v2/hook-form";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { useIsMobile } from "src/hooks/ui";
import { PdHookFormMultipleImagePicker, tImagePickerValue } from "./P09_Profile/hook-form";
import { PdInputGroup, PdInputLeftAddon, tPdInputMultipleProps } from "src/components/v2/forms/input";
import { PdIconButton } from "src/components/forms/icon-button";
import { PdClose } from "src/components/Icons";
import { PdTableCell, PdTableHeader, PdTableHeaderCell, PdTableHeaderRow, PdTableRow } from "src/components/v2/layout";
import { useAppDispatch } from "src/redux";
import { getApiError } from "src/util/apiNew";
import { createBrandProduct, updateBrandProduct } from "src/redux/company/actions";
import { kRoute } from "src/kRoute";

type tParams = {
  params: { id?: string };
  key: string;
  name: string;
};

type tOption<T> = { label: string; value: T };

type tOptionFormData<T extends unknown = string> = tOption<T> | null;

type tProductVariantOptionFormData = {
  name: string;
  labels: string[];
};

type tProductVariantFormData = {
  options: { name: string; label: string }[];
  publish: boolean;
  image?: tImagePickerValue | null;
  sku: string;
  wholesalePrice: string;
  msrpPrice: string;
  inventory: number | null;
  addTester: boolean;
  testerPrice: string;
  testerSku: string;
};

type tProductFormData = {
  name: string;
  description: string;
  category: tOptionFormData<tProductCategory>;
  keywords: string[];
  publish: boolean;
  sku: string;
  upc: string;
  wholesalePrice: string;
  msrpPrice: string;
  caseMinimum: number | null;
  inventory: number | null;
  addTester: boolean;
  testerPrice: string;
  testerSku: string;
  variantOptions: tProductVariantOptionFormData[];
  variants: tProductVariantFormData[];
  images: tImagePickerValue[];
  useAndCare: string;
  ingredients: string;
  materials: string;
  weight: string;
  height: string;
  width: string;
  length: string;
};

const getDefaultValues = (params: { product?: tProduct; categories: tOption<string>[] }): tProductFormData => {
  const { product, categories } = params;
  return {
    name: product?.name ?? "",
    description: product?.description ?? "",
    category: product?.category ? categories.find((category) => category.value === product.category) ?? null : null,
    keywords: product?.keywords.filter((keyword) => !!keyword) ?? [],
    publish: product ? product?.availability === "available" : true,
    sku: product?.sku ?? "",
    upc: product?.upc ?? "",
    wholesalePrice: product?.wholesale_price ? `${product.wholesale_price}` : "",
    msrpPrice: product?.msrp_price ? `${product?.msrp_price}` : "",
    caseMinimum: product?.case_minimum ?? null,
    inventory: product?.inventory ?? null,
    addTester: product?.tester?.price !== undefined,
    testerPrice: product?.tester?.price ? `${product.tester.price}` : "",
    testerSku: product?.tester?.sku ?? "",
    variantOptions: product?.variantOptions ?? [],
    variants:
      product?.variants.map<tProductVariantFormData>((variant) => ({
        options: variant.options ?? [],
        publish: variant.purchasable,
        image: variant.imageUrl ? { uri: variant.imageUrl } : null,
        sku: variant.sku,
        wholesalePrice: `${variant.wholesalePrice}`,
        msrpPrice: `${variant.msrpPrice}`,
        inventory: variant.inventory,
        addTester: !!(variant.tester?.price && variant.tester.sku),
        testerPrice: variant.tester?.price ? `${variant.tester.price}` : "",
        testerSku: variant.tester?.sku ?? "",
      })) ?? [],
    images: product?.images?.map((image) => ({ uri: image.image_url })) ?? [],
    useAndCare: product?.useAndCare ?? "",
    ingredients: product?.ingredients ?? "",
    materials: product?.materials ?? "",
    weight: product?.weight ? `${product.weight}` : "",
    height: product?.height ? `${product.height}` : "",
    width: product?.width ? `${product.width}` : "",
    length: product?.length ? `${product?.length}` : "",
  };
};

const getAllVariantsCombinations = (variantOptions: tProductVariantOptionFormData[]): string[][] => {
  const clonnedVariantOptions = [...variantOptions];

  if (!clonnedVariantOptions.length) {
    return [[]];
  }

  const currentArray = clonnedVariantOptions[0];
  const remainingArrays = clonnedVariantOptions.slice(1);
  const remainingCombinations = getAllVariantsCombinations(remainingArrays);
  const allCombinations = [];

  for (const element of currentArray.labels) {
    for (const combination of remainingCombinations) {
      const newCombination = [element, ...combination];
      allCombinations.push(newCombination);
    }
  }

  return allCombinations;
};

const getVariantOptions = (
  variantOptions: tProductVariantOptionFormData[],
  options: string[],
): { name: string; label: string }[] => {
  const result: { name: string; label: string }[] = [];

  for (const variantOption of variantOptions) {
    for (const option of options) {
      if (variantOption.labels.includes(option)) {
        result.push({ name: variantOption.name, label: option });
      }
    }
  }

  return result;
};

const VariantsTable: React.FC<{ fields: FieldArrayWithId<tProductFormData, "variants", "id">[] }> = ({ fields }) => {
  if (fields.length === 0) {
    return null;
  }

  return (
    <View style={styles.variantsTableContainer}>
      <ScrollView horizontal style={styles.variantsTable}>
        <View>
          <PdTableHeaderRow>
            <PdTableHeaderCell style={styles.variantPublishColumn}>
              <PdTableHeader>Publish</PdTableHeader>
            </PdTableHeaderCell>
            <PdTableHeaderCell style={styles.variantImageColumn}>
              <PdTableHeader>Image</PdTableHeader>
            </PdTableHeaderCell>
            <PdTableHeaderCell style={styles.variantInputColumn}>
              <PdTableHeader>SKU</PdTableHeader>
            </PdTableHeaderCell>
            <PdTableHeaderCell style={styles.variantInputPriceColumn}>
              <PdTableHeader>Wholesale</PdTableHeader>
            </PdTableHeaderCell>
            <PdTableHeaderCell style={styles.variantInputPriceColumn}>
              <PdTableHeader>MSRP</PdTableHeader>
            </PdTableHeaderCell>
            <PdTableHeaderCell style={styles.variantInventoryColumn}>
              <PdTableHeader>Inventory</PdTableHeader>
            </PdTableHeaderCell>
            <PdTableHeaderCell style={styles.variantInputPriceColumn}>
              <PdTableHeader>Tester Price</PdTableHeader>
            </PdTableHeaderCell>
            <PdTableHeaderCell style={styles.variantInputColumn}>
              <PdTableHeader>Tester SKU</PdTableHeader>
            </PdTableHeaderCell>
          </PdTableHeaderRow>
          {fields.map((field, index) => {
            return (
              <PdTableRow key={field.id}>
                <PdTableCell style={styles.variantPublishColumn}>
                  <PdHookFormCheckbox name={`variants.${index}.publish`} />
                </PdTableCell>
                <PdTableCell style={styles.variantImageColumn}>
                  <PdHookFormImagePicker
                    name={`variants.${index}.image`}
                    // rules={{ required: "Required" }}
                    style={styles.variantImagePicker}
                    mobileStyle={styles.variantImagePicker}
                    webStyle={webStyles.variantImagePicker}
                  />
                </PdTableCell>
                <PdTableCell style={styles.variantInputColumn}>
                  <PdHookFormInput
                    name={`variants.${index}.sku`}
                    rules={{ required: `SKU for item ${index + 1} is required` }}
                    preventSpaces
                  />
                </PdTableCell>
                <PdTableCell style={styles.variantInputPriceColumn}>
                  <PdInputGroup>
                    <PdInputLeftAddon>$</PdInputLeftAddon>
                    <PdHookFormInputFloat
                      name={`variants.${index}.wholesalePrice`}
                      rules={{ required: `Wholesale for item ${index + 1} is required` }}
                      style={styles.pdInputGroupInput}
                    />
                  </PdInputGroup>
                </PdTableCell>
                <PdTableCell style={styles.variantInputPriceColumn}>
                  <PdInputGroup>
                    <PdInputLeftAddon>$</PdInputLeftAddon>
                    <PdHookFormInputFloat
                      name={`variants.${index}.msrpPrice`}
                      rules={{ required: `MSRP for item ${index + 1} is required` }}
                      style={styles.pdInputGroupInput}
                    />
                  </PdInputGroup>
                </PdTableCell>
                <PdTableCell style={styles.variantInventoryColumn}>
                  <PdHookFormInputNumber
                    name={`variants.${index}.inventory`}
                    rules={{ required: `Inventory for item ${index + 1} is required` }}
                  />
                </PdTableCell>
                <PdTableCell style={styles.variantInputPriceColumn}>
                  <PdInputGroup>
                    <PdInputLeftAddon>$</PdInputLeftAddon>
                    <PdHookFormInputFloat name={`variants.${index}.testerPrice`} style={styles.pdInputGroupInput} />
                  </PdInputGroup>
                </PdTableCell>
                <PdTableCell style={styles.variantInputColumn}>
                  <PdHookFormInput name={`variants.${index}.testerSku`} />
                </PdTableCell>
              </PdTableRow>
            );
          })}
        </View>
      </ScrollView>
      <PdHookFormErrorMessage name="variants" />
    </View>
  );
};

const Form: React.FC<{ product?: tProduct; categories: tOption<string>[] }> = ({ product, categories }) => {
  const dispatch = useAppDispatch();
  const rBrandId = useSelector((state) =>
    state.CompanyReducer.type === "brand" ? state.CompanyReducer.brandId || 0 : 0,
  );
  const rBrandUid = useSelector((state) =>
    state.CompanyReducer.type === "brand" ? state.CompanyReducer.id || "" : "",
  );
  const rBrandName = useSelector((state) =>
    state.CompanyReducer.type === "brand" ? state.CompanyReducer.name || "" : "",
  );
  const isMobile = useIsMobile();
  const navigation = useNavigation();
  const formMethods = useForm<tProductFormData>({
    defaultValues: getDefaultValues({ product, categories }),
  });
  const variantOptionsFieldArray = useFieldArray({
    control: formMethods.control,
    name: "variantOptions",
  });
  const variantsFieldArray = useFieldArray({
    control: formMethods.control,
    name: "variants",
  });
  const [wholesalePrice, msrpPrice, addTester] = useWatch({
    name: ["wholesalePrice", "msrpPrice", "addTester"],
    control: formMethods.control,
  });
  const [isSubmitting, setSubmitting] = React.useState(false);

  const hasVariants = variantsFieldArray.fields.length > 0;

  const handleOnSubmit = formMethods.handleSubmit(async (formData) => {
    try {
      setSubmitting(true);

      const category = formData.category?.value ?? categories[0].value;
      const payload: tProduct = {
        ...product,
        name: formData.name,
        description: formData.description,
        category,
        categoryHierarchy: {
          lvl0: category.split("-")?.[0]?.trim() ?? "",
          lvl1: category ?? "",
        },
        keywords: formData.keywords,
        availability: formData.publish ? "available" : "unavailable",
        sku: formData.sku,
        upc: formData.upc,
        wholesale_price: formData.wholesalePrice ? parseFloat(formData.wholesalePrice) : 0,
        msrp_price: formData.msrpPrice ? parseFloat(formData.msrpPrice) : 0,
        case_minimum: formData.caseMinimum || 1,
        inventory: formData.inventory || 0,
        variantOptions: formData.variantOptions,
        variants: [],
        useAndCare: formData.useAndCare,
        ingredients: formData.ingredients,
        materials: formData.materials,
        weight: formData.weight,
        height: formData.height,
        width: formData.width,
        length: formData.length,
        // internal mandatory values
        brandId: rBrandId,
        brandUid: rBrandUid,
        brandName: rBrandName,
        timeCreated: product ? product.timeCreated : firebase.firestore.Timestamp.now(),
        timeUpdated: firebase.firestore.Timestamp.now(),
        processing_time: "",
        shipping_terms: "",
        return_policy: "",
        productId: product?.productId || "",
      };

      if (formData.addTester) {
        payload.tester = { price: parseFloat(formData.testerPrice), sku: formData.testerSku };
      }

      const variants: tVariant[] = [];
      for (const variant of formData.variants) {
        let imageUrl = variant.image?.uri;
        if (variant.image?.file) {
          const fileExtension = variant.image.file.type.replace(/^.*\//gm, "");
          const snapshot = await firebase
            .storage()
            .ref(`brands/${payload.brandUid}/products/variant${uuidv4()}.${fileExtension}`)
            .put(variant.image.file);
          imageUrl = await snapshot.ref.getDownloadURL();
        }

        const mappedVariant: tVariant = {
          options: variant.options,
          imageUrl,
          inventory: variant.inventory || 0,
          sku: variant.sku,
          wholesalePrice: parseFloat(variant.wholesalePrice),
          msrpPrice: parseFloat(variant.msrpPrice),
          purchasable: variant.publish,
        };

        if (variant.testerPrice && variant.testerSku) {
          mappedVariant.tester = { price: parseFloat(variant.testerPrice), sku: variant.testerSku };
        }

        variants.push(mappedVariant);
      }
      payload.variants = variants;

      const images: tImage[] = [];
      for (const image of formData.images) {
        if (image.uri?.startsWith("https://")) {
          images.push({ image_url: image.uri });
          continue;
        }
        if (!image.file) continue;
        const fileExtension = image.file.type.replace(/^.*\//gm, "");
        const snapshot = await firebase
          .storage()
          .ref(`brands/${payload.brandUid}/products/product${uuidv4()}.${fileExtension}`)
          .put(image.file);
        const url = await snapshot.ref.getDownloadURL();
        images.push({ image_url: url });
      }
      payload.images = images;

      if (product?.productId) {
        await dispatch(updateBrandProduct(payload));
      } else {
        await dispatch(createBrandProduct(payload));
      }

      navigation.navigate(kRoute.P08);
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error(getApiError(error).message) });
    } finally {
      setSubmitting(false);
    }
  });

  const handleOnChangeVariantValues: tPdInputMultipleProps["onChangeText"] = () => {
    const currentVariants = formMethods.getValues("variants");
    const currentVariantOptions = formMethods.getValues("variantOptions");
    const validVariantOptions = currentVariantOptions?.filter((option) => option.labels.length > 0) ?? [];

    if (validVariantOptions.length === 0) {
      variantsFieldArray.replace([]);
      return;
    }

    const options = getAllVariantsCombinations(validVariantOptions);
    const newVariants: tProductVariantFormData[] = [];

    formMethods.setValue("wholesalePrice", "");
    formMethods.setValue("msrpPrice", "");
    formMethods.setValue("inventory", null);

    for (const option of options) {
      const maybeAddedVariant = currentVariants.find((variant) =>
        variant.options.find((variantOption) => option.includes(variantOption.label)),
      );
      const newVariantOptions = getVariantOptions(validVariantOptions, option);
      newVariants.push({
        options: newVariantOptions,
        publish: maybeAddedVariant ? maybeAddedVariant.publish : true,
        sku: newVariantOptions
          .map((newVariantOption) => newVariantOption.label.replace(/\s+/g, "-"))
          .join("-")
          .toUpperCase(),
        wholesalePrice: maybeAddedVariant ? maybeAddedVariant.wholesalePrice : "",
        msrpPrice: maybeAddedVariant ? maybeAddedVariant.msrpPrice : "",
        inventory: maybeAddedVariant ? maybeAddedVariant.inventory : null,
        addTester: maybeAddedVariant ? maybeAddedVariant.addTester : false,
        testerPrice: maybeAddedVariant ? maybeAddedVariant.testerPrice : "",
        testerSku: maybeAddedVariant ? maybeAddedVariant.testerSku : "",
      });
    }

    variantsFieldArray.replace(newVariants);
  };

  const { reset } = formMethods;
  useFocusEffect(
    React.useCallback(() => {
      reset(getDefaultValues({ product, categories }));
    }, [categories, product, reset]),
  );

  return (
    <FormProvider {...formMethods}>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3} style={styles.formContainer}>
        <View>
          <PdFormCard style={styles.formSectionDescription}>
            <View style={styles.formSectionTitle}>
              <PdSectionBadge>
                <PdSectionBadgeLabel>1</PdSectionBadgeLabel>
              </PdSectionBadge>
              <PdText variant="fieldset" style={styles.ml2}>
                Description
              </PdText>
            </View>
            <PdDivider style={styles.formSectionDivider} />
            <View style={styles.mb3}>
              <PdFormLabel>Product Name *</PdFormLabel>
              <PdHookFormInput name="name" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
              <PdHookFormErrorMessage name="name" />
            </View>
            <View>
              <PdFormLabel>Product Description *</PdFormLabel>
              <PdHookFormInput
                name="description"
                rules={{ required: "Required" }}
                multiline
                numberOfLines={12}
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="description" />
            </View>
          </PdFormCard>

          <PdFormCard style={styles.formSectionCategory}>
            <View style={styles.formSectionTitle}>
              <PdSectionBadge>
                <PdSectionBadgeLabel>2</PdSectionBadgeLabel>
              </PdSectionBadge>
              <PdText variant="fieldset" style={styles.ml2}>
                Category
              </PdText>
            </View>
            <PdDivider style={styles.formSectionDivider} />
            <PdStack direction="vertical">
              <View style={styles.formControlCategory}>
                <PdFormLabel>Category *</PdFormLabel>
                <PdHookFormSelect
                  name="category"
                  rules={{ required: "Required" }}
                  placeholder="E.g. Gift Shop"
                  options={categories}
                  onSubmitEditing={handleOnSubmit}
                />
                <PdHookFormErrorMessage name="category" />
              </View>
              <View>
                <PdFormLabel>Keywords</PdFormLabel>
                <PdHookFormInputMultiple name="keywords" />
              </View>
            </PdStack>
          </PdFormCard>

          <PdFormCard style={styles.formSectionPriceFulfillment}>
            <View style={styles.formSectionTitle}>
              <PdSectionBadge>
                <PdSectionBadgeLabel>3</PdSectionBadgeLabel>
              </PdSectionBadge>
              <PdText variant="fieldset" style={styles.ml2}>
                Price & Fulfillment
              </PdText>
            </View>
            <PdDivider style={styles.formSectionDivider} />
            <PdStack direction="vertical">
              <View>
                <PdFormLabel>Would you like to publish the product?</PdFormLabel>
                <PdRow style={hasVariants ? styles.pdDisabledContainer : {}}>
                  <PdText variant="body-sm">No</PdText>
                  <PdHookFormSwitch name="publish" isDisabled={hasVariants} style={styles.yesNoSwitchField} />
                  <PdText variant="body-sm">Yes</PdText>
                </PdRow>
              </View>
              <View>
                <PdFormLabel>SKU *</PdFormLabel>
                <PdHookFormInput name="sku" rules={{ required: "Required" }} preventSpaces />
                <PdHookFormErrorMessage name="sku" />
              </View>
              <View>
                <PdFormLabel>UPC</PdFormLabel>
                <PdHookFormInput name="upc" />
              </View>
              <View>
                <PdFormLabel>{hasVariants ? "Wholesale" : "Wholesale *"}</PdFormLabel>
                <PdInputGroup style={hasVariants ? styles.pdDisabledContainer : {}}>
                  <PdInputLeftAddon>$</PdInputLeftAddon>
                  <PdHookFormInputFloat
                    name="wholesalePrice"
                    isDisabled={hasVariants}
                    rules={{ required: !hasVariants && "Required" }}
                    style={styles.pdInputGroupInput}
                  />
                </PdInputGroup>
                <PdHookFormErrorMessage name="wholesalePrice" />
              </View>
              <View>
                <PdFormLabel>{hasVariants ? "MSRP" : "MSRP *"}</PdFormLabel>
                <PdInputGroup style={hasVariants ? styles.pdDisabledContainer : {}}>
                  <PdInputLeftAddon>$</PdInputLeftAddon>
                  <PdHookFormInputFloat
                    name="msrpPrice"
                    isDisabled={hasVariants}
                    rules={{ required: !hasVariants && "Required" }}
                    style={styles.pdInputGroupInput}
                  />
                </PdInputGroup>
                <PdHookFormErrorMessage name="msrpPrice" />
                {wholesalePrice && msrpPrice && parseFloat(wholesalePrice) > parseFloat(msrpPrice) && (
                  <PdFormErrorText>Are you sure you have your pricing correct?</PdFormErrorText>
                )}
              </View>
              <View>
                <PdFormLabel>Case Minimum *</PdFormLabel>
                <PdHookFormInputNumber name="caseMinimum" rules={{ required: "Required" }} />
                <PdHookFormErrorMessage name="caseMinimum" />
              </View>
              <View>
                <PdFormLabel>{hasVariants ? "Inventory Available" : "Inventory Available *"}</PdFormLabel>
                <PdHookFormInputNumber
                  name="inventory"
                  isDisabled={hasVariants}
                  rules={{ required: !hasVariants && "Required" }}
                />
                <PdHookFormErrorMessage name="inventory" />
              </View>
            </PdStack>
          </PdFormCard>

          <PdFormCard style={styles.formSectionTesters}>
            <View style={styles.formSectionTitle}>
              <PdSectionBadge>
                <PdSectionBadgeLabel>4</PdSectionBadgeLabel>
              </PdSectionBadge>
              <PdText variant="fieldset" style={styles.ml2}>
                Testers
              </PdText>
            </View>
            <PdDivider style={styles.formSectionDivider} />
            <PdStack direction="vertical">
              <View>
                <PdFormLabel>Would you like to include a tester?</PdFormLabel>
                <PdRow style={hasVariants ? styles.pdDisabledContainer : {}}>
                  <PdText variant="body-sm">No</PdText>
                  <PdHookFormSwitch
                    name="addTester"
                    isDisabled={hasVariants}
                    style={styles.yesNoSwitchField}
                    onValueChange={() => formMethods.setValue("testerSku", "-TESTER")}
                  />
                  <PdText variant="body-sm">Yes</PdText>
                </PdRow>
              </View>
              {addTester && (
                <PdStack direction="vertical">
                  <View>
                    <PdFormLabel>Price *</PdFormLabel>
                    <PdInputGroup>
                      <PdInputLeftAddon>$</PdInputLeftAddon>
                      <PdHookFormInputFloat
                        name="testerPrice"
                        rules={{ required: "Required" }}
                        style={styles.pdInputGroupInput}
                      />
                    </PdInputGroup>
                  </View>
                  <View>
                    <PdFormLabel>SKU *</PdFormLabel>
                    <PdHookFormInput name="testerSku" rules={{ required: "Required" }} preventSpaces />
                    <PdHookFormErrorMessage name="testerSku" />
                  </View>
                </PdStack>
              )}
            </PdStack>
          </PdFormCard>

          <PdFormCard>
            <View style={styles.formSectionTitle}>
              <PdSectionBadge>
                <PdSectionBadgeLabel>5</PdSectionBadgeLabel>
              </PdSectionBadge>
              <PdText variant="fieldset" style={styles.ml2}>
                Variants
              </PdText>
            </View>
            <PdDivider style={styles.formSectionDivider} />
            {variantOptionsFieldArray.fields.map((field, index) => {
              return (
                <PdRow key={field.id} style={styles.variantRow}>
                  <PdText variant="body-lg" style={styles.variantRowIndex}>{`${index + 1}.`}</PdText>
                  <PdDivider isVertical style={styles.variantRowSeparator} />
                  <PdStack direction="vertical" style={styles.flex1}>
                    <View>
                      <PdFormLabel>Option</PdFormLabel>
                      <PdHookFormInput
                        name={`variantOptions.${index}.name`}
                        placeholder="example: size, color"
                        rules={{ required: "Required" }}
                      />
                    </View>
                    <View>
                      <PdFormLabel>Values</PdFormLabel>
                      <PdHookFormInputMultiple
                        name={`variantOptions.${index}.labels`}
                        placeholder="example: small, large, red, blue"
                        rules={{ required: "Required" }}
                        onChangeText={handleOnChangeVariantValues}
                      />
                    </View>
                  </PdStack>
                  <PdIconButton
                    variant="ghost"
                    contentContainerStyle={styles.removeButton}
                    onPress={() => {
                      variantOptionsFieldArray.remove(index);
                      handleOnChangeVariantValues([]);
                    }}
                    icon={<PdClose style={styles.removeButtonIcon} />}
                  />
                </PdRow>
              );
            })}
            <VariantsTable fields={variantsFieldArray.fields} />
            <PdButton onPress={() => variantOptionsFieldArray.append({ name: "", labels: [] })} style={styles.mrAuto}>
              <PdButtonLabel>Add Variant</PdButtonLabel>
            </PdButton>
          </PdFormCard>
        </View>
        <View>
          <PdFormCard style={styles.formSectionImages}>
            <View style={styles.formSectionTitle}>
              <PdSectionBadge>
                <PdSectionBadgeLabel>6</PdSectionBadgeLabel>
              </PdSectionBadge>
              <PdText variant="fieldset" style={styles.ml2}>
                Select Images
              </PdText>
            </View>
            <PdDivider style={styles.formSectionDivider} />
            <View>
              <PdHookFormMultipleImagePicker
                name="images"
                rules={{
                  validate: (value: string[]) => (value.length === 0 ? "You need to select at least 1 image" : true),
                }}
                length={6}
              />
              <PdHookFormErrorMessage name="images" />
            </View>
          </PdFormCard>
          <PdFormCard style={styles.formSectionDetails}>
            <View style={styles.formSectionTitle}>
              <PdSectionBadge>
                <PdSectionBadgeLabel>7</PdSectionBadgeLabel>
              </PdSectionBadge>
              <PdText variant="fieldset" style={styles.ml2}>
                Product Details
              </PdText>
            </View>
            <PdDivider style={styles.formSectionDivider} />
            <PdStack direction="vertical">
              <View>
                <PdFormLabel>Use & Care</PdFormLabel>
                <PdHookFormInput name="useAndCare" />
              </View>
              <View>
                <PdFormLabel>Ingredients</PdFormLabel>
                <PdHookFormInput name="ingredients" />
              </View>
              <View>
                <PdFormLabel>Materials</PdFormLabel>
                <PdHookFormInput name="materials" />
              </View>
            </PdStack>
          </PdFormCard>
          <PdFormCard style={styles.formSectionDimensions}>
            <View style={styles.formSectionTitle}>
              <PdSectionBadge>
                <PdSectionBadgeLabel>8</PdSectionBadgeLabel>
              </PdSectionBadge>
              <PdText variant="fieldset" style={styles.ml2}>
                Dimensions *
              </PdText>
              <PdText variant="body-xs" style={styles.formSectionDimensionsHelperText}>
                If not applicable, write N/A
              </PdText>
            </View>
            <PdDivider style={styles.formSectionDivider} />
            <PdStack direction="vertical">
              <View>
                <PdFormLabel>Weight</PdFormLabel>
                <PdHookFormInput name="weight" rules={{ required: "Required" }} />
                <PdHookFormErrorMessage name="weight" />
              </View>
              <View>
                <PdFormLabel>Height</PdFormLabel>
                <PdHookFormInput name="height" rules={{ required: "Required" }} />
                <PdHookFormErrorMessage name="height" />
              </View>
              <View>
                <PdFormLabel>Width</PdFormLabel>
                <PdHookFormInput name="width" rules={{ required: "Required" }} />
                <PdHookFormErrorMessage name="width" />
              </View>
              <View>
                <PdFormLabel>Length</PdFormLabel>
                <PdHookFormInput name="length" rules={{ required: "Required" }} />
                <PdHookFormErrorMessage name="length" />
              </View>
            </PdStack>
          </PdFormCard>
          <PdButton isLoading={isSubmitting} onPress={handleOnSubmit} style={styles.mlAuto}>
            <PdButtonLabel>Save Changes</PdButtonLabel>
          </PdButton>
        </View>
      </PdStack>
    </FormProvider>
  );
};

const P08_01_Product: React.FC<{ navigation: NavigationProp<ParamListBase> }> = () => {
  const route = useRoute<tParams>();
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const rConfigShop = useSelector((state) => state.ConfigsReducer.data.shop);
  const [product, setProduct] = React.useState<tProduct | undefined>(undefined);

  const categories = React.useMemo(() => {
    const _categories: tOption<string>[] = [];
    if (rConfigShop?.productCategories) {
      for (const firstLevel of Object.keys(rConfigShop.productCategories)) {
        for (const secondLevel of rConfigShop.productCategories[firstLevel]) {
          _categories.push({ value: `${firstLevel} - ${secondLevel}`, label: `${firstLevel} - ${secondLevel}` });
        }
      }
    }
    return _categories.sort((c1, c2) => c1.value.localeCompare(c2.value));
  }, [rConfigShop?.productCategories]);

  useFocusEffect(
    React.useCallback(() => {
      let unsubscribe: (() => void) | undefined;

      if (rCompanyId && route.params.id) {
        unsubscribe = documentBrandProduct(rCompanyId, route.params.id).onSnapshot((document) => {
          setProduct(document.data());
        });
      } else {
        setProduct(undefined);
      }

      return () => {
        unsubscribe?.();
      };
    }, [rCompanyId, route.params.id]),
  );

  if (route.params.id && !product) {
    return (
      <PdCenter>
        <ActivityIndicator color={kColors.brandGreen} />
      </PdCenter>
    );
  }

  return (
    <ScrollView style={styles.page}>
      <Form product={product} categories={categories} />
    </ScrollView>
  );
};

export const P08_01 = React.memo(P08_01_Product);

const styles = StyleSheet.create({
  flex1: {
    flex: 1,
  },
  formContainer: {
    width: "100%",
    zIndex: 1,
  },
  formControlCategory: {
    zIndex: 1,
  },
  formSectionCategory: {
    marginBottom: kStyleConst.kSpacing * 4,
    zIndex: 1,
  },
  formSectionDescription: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  formSectionDetails: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  formSectionDimensions: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  formSectionDimensionsHelperText: {
    color: kColors.grayPink,
    marginLeft: "auto",
  },
  formSectionDivider: {
    marginBottom: 4 * kStyleConst.kSpacing,
    marginTop: 1 * kStyleConst.kSpacing,
  },
  formSectionImages: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  formSectionPriceFulfillment: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  formSectionTesters: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  formSectionTitle: {
    alignItems: "center",
    flexDirection: "row",
  },
  mb3: {
    marginBottom: kStyleConst.kSpacing * 3,
  },
  ml2: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mrAuto: {
    marginRight: "auto",
  },
  page: {
    backgroundColor: kColors.lightGray,
    padding: kStyleConst.kSpacing * 6,
  },
  pdDisabledContainer: {
    opacity: 0.4,
  },
  pdInputGroupInput: {
    borderWidth: 0,
    width: "100%",
  },
  removeButton: {
    height: 37,
    marginLeft: kStyleConst.kSpacing * 4,
    width: 37,
  },
  removeButtonIcon: {
    height: 15,
    width: 15,
  },
  variantImageColumn: {
    justifyContent: "center",
    width: 55,
  },
  variantImagePicker: {
    alignItems: "center",
    backgroundColor: kColors.lightGray,
    height: 34,
    justifyContent: "center",
    margin: kStyleConst.kSpacing,
    width: 34,
  },
  variantInputColumn: {
    justifyContent: "flex-start",
    width: 200,
  },
  variantInputPriceColumn: {
    justifyContent: "flex-start",
    width: 100,
  },
  variantInventoryColumn: {
    justifyContent: "flex-start",
    width: 80,
  },
  variantPublishColumn: {
    justifyContent: "center",
    width: 55,
  },
  variantRow: {
    alignItems: "flex-start",
    marginBottom: kStyleConst.kSpacing * 4,
  },
  variantRowIndex: {
    width: 30,
  },
  variantRowSeparator: {
    marginHorizontal: kStyleConst.kSpacing * 4,
  },
  variantsTable: {
    borderColor: kColors.gray300,
    borderRadius: 5,
    borderWidth: 1,
    // marginBottom: kStyleConst.kSpacing * 3,
  },
  variantsTableContainer: {
    marginBottom: kStyleConst.kSpacing * 3,
  },
  yesNoSwitchField: {
    marginHorizontal: 1 * kStyleConst.kSpacing,
    transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }],
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  variantImagePicker: {
    alignItems: "center",
    backgroundColor: kColors.grayPink,
    height: 30,
    justifyContent: "center",
    margin: kStyleConst.kSpacing,
    width: 30,
  },
} as const;
